import React from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceStatusDashboard = ({ currentLanguage }) => {
  const complianceItems = [
    {
      id: 'gdpr',
      title: 'GDPR Compliance',
      status: 'compliant',
      lastAudit: '2024-09-15',
      nextReview: '2024-12-15',
      description: 'General Data Protection Regulation compliance status',
      icon: 'Shield',
      details: ['Data mapping completed', 'Privacy policies updated', 'Consent mechanisms active']
    },
    {
      id: 'cookies',
      title: 'Cookie Compliance',
      status: 'compliant',
      lastAudit: '2024-10-01',
      nextReview: '2025-01-01',
      description: 'Cookie consent and management system status',
      icon: 'Cookie',
      details: ['Consent banner active', 'Preference center functional', 'Cookie categorization complete']
    },
    {
      id: 'bafin',
      title: 'BaFin Regulatory',
      status: 'compliant',
      lastAudit: '2024-08-20',
      nextReview: '2025-02-20',
      description: 'German financial services regulation compliance',
      icon: 'Building2',
      details: ['License current', 'Reporting up to date', 'Capital requirements met']
    },
    {
      id: 'mifid',
      title: 'MiFID II Compliance',
      status: 'review',
      lastAudit: '2024-07-10',
      nextReview: '2024-11-10',
      description: 'Markets in Financial Instruments Directive compliance',
      icon: 'TrendingUp',
      details: ['Documentation review pending', 'Client categorization updated', 'Best execution policies active']
    },
    {
      id: 'aml',
      title: 'AML/KYC Procedures',
      status: 'compliant',
      lastAudit: '2024-09-30',
      nextReview: '2024-12-30',
      description: 'Anti-Money Laundering and Know Your Customer compliance',
      icon: 'UserCheck',
      details: ['KYC procedures updated', 'Transaction monitoring active', 'Staff training completed']
    },
    {
      id: 'data_security',
      title: 'Data Security Standards',
      status: 'compliant',
      lastAudit: '2024-10-10',
      nextReview: '2025-01-10',
      description: 'Information security and data protection measures',
      icon: 'Lock',
      details: ['Encryption protocols active', 'Access controls implemented', 'Backup systems operational']
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant':
        return 'text-success bg-success/10 border-success/20';
      case 'review':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'action_required':
        return 'text-destructive bg-destructive/10 border-destructive/20';
      default:
        return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return 'CheckCircle';
      case 'review':
        return 'AlertTriangle';
      case 'action_required':
        return 'XCircle';
      default:
        return 'Clock';
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      en: {
        compliant: 'Compliant',
        review: 'Under Review',
        action_required: 'Action Required'
      },
      de: {
        compliant: 'Konform',
        review: 'In Überprüfung',
        action_required: 'Maßnahmen Erforderlich'
      },
      fr: {
        compliant: 'Conforme',
        review: 'En Révision',
        action_required: 'Action Requise'
      },
      es: {
        compliant: 'Conforme',
        review: 'En Revisión',
        action_required: 'Acción Requerida'
      }
    };
    return statusTexts?.[currentLanguage]?.[status] || statusTexts?.en?.[status];
  };

  const overallCompliance = Math.round(
    (complianceItems?.filter(item => item?.status === 'compliant')?.length / complianceItems?.length) * 100
  );

  return (
    <div className="bg-card border border-border rounded-brand p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-primary/10 rounded-brand-sm">
            <Icon name="Shield" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Compliance Status Dashboard</h2>
            <p className="text-sm text-muted-foreground">Real-time regulatory compliance monitoring</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-2xl font-bold text-foreground">{overallCompliance}%</div>
          <div className="text-sm text-muted-foreground">Overall Compliance</div>
        </div>
      </div>
      {/* Overall Status Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Compliance Score</span>
          <span className="text-sm text-muted-foreground">{overallCompliance}/100</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-success to-primary h-2 rounded-full transition-brand"
            style={{ width: `${overallCompliance}%` }}
          ></div>
        </div>
      </div>
      {/* Compliance Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {complianceItems?.map((item) => (
          <div key={item?.id} className="border border-border rounded-brand p-4 hover-lift transition-brand">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-muted rounded-brand-sm">
                  <Icon name={item?.icon} size={18} className="text-foreground" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground text-sm">{item?.title}</h3>
                </div>
              </div>
              
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-brand-xs border text-xs font-medium ${getStatusColor(item?.status)}`}>
                <Icon name={getStatusIcon(item?.status)} size={12} />
                <span>{getStatusText(item?.status)}</span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{item?.description}</p>

            <div className="space-y-2 mb-3">
              {item?.details?.map((detail, index) => (
                <div key={index} className="flex items-center space-x-2 text-xs">
                  <div className="w-1 h-1 bg-primary rounded-full"></div>
                  <span className="text-muted-foreground">{detail}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border">
              <span>Last: {new Date(item.lastAudit)?.toLocaleDateString()}</span>
              <span>Next: {new Date(item.nextReview)?.toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>
      {/* Quick Actions */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="font-medium text-foreground mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button className="flex items-center space-x-2 p-3 bg-muted/50 rounded-brand hover:bg-muted transition-brand text-left">
            <Icon name="Download" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Export Report</span>
          </button>
          
          <button className="flex items-center space-x-2 p-3 bg-muted/50 rounded-brand hover:bg-muted transition-brand text-left">
            <Icon name="Calendar" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Schedule Audit</span>
          </button>
          
          <button className="flex items-center space-x-2 p-3 bg-muted/50 rounded-brand hover:bg-muted transition-brand text-left">
            <Icon name="AlertTriangle" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">View Alerts</span>
          </button>
          
          <button className="flex items-center space-x-2 p-3 bg-muted/50 rounded-brand hover:bg-muted transition-brand text-left">
            <Icon name="Settings" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Configure</span>
          </button>
        </div>
      </div>
      {/* Last Updated */}
      <div className="mt-4 text-center">
        <p className="text-xs text-muted-foreground">
          Dashboard last updated: {new Date()?.toLocaleString()} • Auto-refresh every 15 minutes
        </p>
      </div>
    </div>
  );
};

export default ComplianceStatusDashboard;